import { useEffect, useMemo, useState } from "react";
import { clusterQuestions, clusterPriority } from "./clusterQuestions";

/**
 * The clusters the rail shows.
 *
 * Grouping runs locally by default. `groupWith` lets a caller hand the job to
 * the API instead — it takes the same questions and returns the same cluster
 * shape, so swapping to a server-side model changes nothing below this line.
 * If it fails, the local pass still stands: a room mid-lesson should never
 * lose its question monitor because a request timed out.
 */
export function useQuestionClusters(questions, { groupWith, refreshMs = 15000 } = {}) {
  const local = useMemo(() => clusterQuestions(questions), [questions]);
  const [remote, setRemote] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  // The rail shows how long a class has waited, so it has to re-render on a
  // clock as well as on new messages.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (typeof groupWith !== "function" || questions.length === 0) {
      setRemote(null);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const grouped = await groupWith(questions);
        if (!cancelled && Array.isArray(grouped)) setRemote(grouped);
      } catch (e) {
        console.error("Question grouping failed; keeping the local grouping.", e);
      }
    };
    run();
    const id = setInterval(run, refreshMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [groupWith, questions, refreshMs]);

  const clusters = remote || local;

  return useMemo(
    () => ({
      clusters: [...clusters].sort((a, b) => clusterPriority(b, now) - clusterPriority(a, now)),
      now,
      isGrouped: Boolean(remote),
    }),
    [clusters, now, remote]
  );
}
